import { useEvent } from '@emerald-works/react-event-bus-client'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useGroup } from '../../../hooks'
import { announcementsSlice } from '../../../reducers'
import { NOT_FOUND_ERROR } from '../../../reducers/announcements/slice'
import { ALL_ENTERPRISE } from '../../../services/cognito/userGroups'
import {
  ANNOUNCEMENT_AUDIENCE,
  WHATS_NEW_API_ENABLED,
  WHATS_NEW_PAGE_SIZE
} from '../constants'
import { buildMockAnnouncementsPage, findMockAnnouncement } from '../mock-announcements'

/** Fake round-trip so the loading states are actually exercised in mock mode. */
const MOCK_LATENCY_MS = 500

/** Which audience axis this session falls on. Only the mock needs it. */
const useAnnouncementAudience = () => {
  const { isInGroup } = useGroup()
  return isInGroup(ALL_ENTERPRISE) ? ANNOUNCEMENT_AUDIENCE.ENTERPRISE : ANNOUNCEMENT_AUDIENCE.CONSUMER
}

/**
 * Owns the What's new data. Reads through the `announcements` slice so the
 * page, the home rail and the nav badge share one source of truth and one
 * request. Same shape as the notification centre's `useNotifications`.
 *
 * `eager` loads the first page on mount when nothing is loaded yet — the home
 * rail and the toolbar badge need it before the page is ever visited.
 */
export const useAnnouncements = ({ eager = false } = {}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const { actions, selectors, eventBus } = announcementsSlice
  const audience = useAnnouncementAudience()

  const items = useSelector(selectors.selectAnnouncements)
  const total = useSelector(selectors.selectTotal)
  const hasMore = useSelector(selectors.selectHasMore)
  const loading = useSelector(selectors.selectLoading)
  const loadingMore = useSelector(selectors.selectLoadingMore)
  const error = useSelector(selectors.selectError)
  const hasLoaded = useSelector(selectors.selectHasLoaded)
  const latestPublishedAt = useSelector(selectors.selectLatestPublishedAt)
  const current = useSelector(selectors.selectCurrent)
  const currentLoading = useSelector(selectors.selectCurrentLoading)
  const currentError = useSelector(selectors.selectCurrentError)

  const [listAnnouncements, retrieveAnnouncement] = useEvent([
    eventBus.listAnnouncements,
    eventBus.retrieveAnnouncement
  ])

  const mockTimer = useRef(null)
  const mockRetrieveTimer = useRef(null)
  // Only the detail timer is cancelled on unmount: that page resets its own
  // state on the way out (`clearCurrent`). The list timer must survive it —
  // the fetch is shared and dedupes on the store, so if the instance that
  // started it left before the answer, cancelling would strand `loading: true`
  // with every other instance waiting on a request that will never resolve.
  useEffect(() => () => clearTimeout(mockRetrieveTimer.current), [])

  const fetchPage = useCallback(({ from }) => {
    // One request at a time, whoever asks: the rail, the toolbar and the page
    // can all mount in the same commit. Read off the store because the
    // rendered `loading` is still false inside that commit.
    const state = store.getState()
    if (selectors.selectLoading(state) || selectors.selectLoadingMore(state)) return

    dispatch(from > 0 ? actions.setLoadingMore() : actions.setLoading())
    const params = { from, size: WHATS_NEW_PAGE_SIZE }

    if (WHATS_NEW_API_ENABLED) {
      listAnnouncements.trigger(params)
      return
    }
    // MOCK PATH — dispatches the same action the bus would.
    clearTimeout(mockTimer.current)
    mockTimer.current = setTimeout(() => {
      dispatch(actions.setAnnouncements(buildMockAnnouncementsPage({ ...params, audience })))
    }, MOCK_LATENCY_MS)
  }, [dispatch, actions, selectors, store, listAnnouncements, audience])

  const load = useCallback(() => fetchPage({ from: 0 }), [fetchPage])

  // `from` is how much is already held rather than a page counter.
  const loadMore = useCallback(() => {
    const state = store.getState()
    if (!selectors.selectHasMore(state)) return
    fetchPage({ from: selectors.selectAnnouncements(state).length })
  }, [fetchPage, selectors, store])

  const retrieve = useCallback(id => {
    if (!id) return
    dispatch(actions.setCurrentLoading(id))

    if (WHATS_NEW_API_ENABLED) {
      retrieveAnnouncement.trigger({ id })
      return
    }
    clearTimeout(mockRetrieveTimer.current)
    mockRetrieveTimer.current = setTimeout(() => {
      const found = findMockAnnouncement(id, { audience })
      dispatch(found ? actions.setCurrent(found) : actions.setCurrentError(NOT_FOUND_ERROR))
    }, MOCK_LATENCY_MS)
  }, [dispatch, actions, retrieveAnnouncement, audience])

  const clearCurrent = useCallback(() => dispatch(actions.clearCurrent()), [dispatch, actions])

  useEffect(() => {
    if (!eager || hasLoaded) return
    if (selectors.selectHasLoaded(store.getState())) return
    load()
  }, [eager, hasLoaded, selectors, store, load])

  return {
    items,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    hasLoaded,
    latestPublishedAt,
    load,
    loadMore,
    retrieve,
    clearCurrent,
    current,
    currentLoading,
    currentError
  }
}

export default useAnnouncements
